import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FormDialog, FormField } from '../../../components/molecules';
import type { Doctor, DoctorPatientRelation, PaymentRequestData, Product } from '../../../types/ninox.types';
import ProductService from '../../../services/product.service';
import PaymentService from '../../../services/payment.service';
import { DoctorService } from '../../../services/doctor.service';
import { useIntl } from 'react-intl';

type CreatePaymentModalProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  patientId: number;
  patientDoctorsList: DoctorPatientRelation[];
  onSuccess: () => void;
};

interface NewPaymentState {
  productCode: string;
  appointmentId: string;
  followupPrescriptionId: string;
  doctorId: string;
  couponCode: string;
}

const emptyNewPayment: NewPaymentState = {
  productCode: '',
  appointmentId: '',
  followupPrescriptionId: '',
  doctorId: '',
  couponCode: '',
};

const DISCOUNT_CODE_CHOICES: [string, string][] = [
  ['', 'No discount'],
  ['SORRY50', 'SORRY50'],
];

export function CreatePaymentModal({
  show,
  setShow,
  patientId,
  patientDoctorsList,
  onSuccess,
}: CreatePaymentModalProps) {
  const [formData, setFormData] = useState<NewPaymentState>(emptyNewPayment);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectableProducts, setSelectableProducts] = useState<Product[]>([]);
  const [allDoctors, setAllDoctors] = useState<Doctor[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const intl = useIntl();

  const isAppointment = selectedProduct?.code.toLowerCase().includes('appointment');
  const isPrescription = selectedProduct?.code.toLowerCase().includes('prescription');
  const isDoctorPaymentFalse = selectedProduct?.isDoctorPayment === false;

  useEffect(() => {
    const fetchProducts = async () => {
      const response = await ProductService.getProducts();
      if (!response.error && Array.isArray(response.data)) {
        setProducts(response.data);
      } else {
        console.error('Failed to fetch products:', response.message);
        setErrorMessage(intl.formatMessage({ id: 'patient.error.productsLoad' }));
      }
    };

    const fetchAllDoctors = async () => {
      const response = await DoctorService.getAllDoctors();
      if (!response.error && Array.isArray(response.data)) {
        setAllDoctors(response.data);
      } else {
        console.error('Failed to fetch doctors:', response.message);
        setErrorMessage(intl.formatMessage({ id: 'patient.error.doctorsLoad' }));
      }
    };

    if (show) {
      const mainDoctorRelation = patientDoctorsList.find((rel) => rel.doctorType === 1);
      const mainDoctorId = mainDoctorRelation ? mainDoctorRelation.doctorId.toString() : '';

      setFormData({
        ...emptyNewPayment,
        doctorId: mainDoctorId,
      });

      setErrorMessage('');
      fetchProducts();
      fetchAllDoctors();
    }
  }, [show, patientDoctorsList, intl]);

  useEffect(() => {
    const filteredData = products.filter(
      (product: Product) => product.isDoctorPayment === true || product.isDoctorPayment === false
    );

    const sortedData = filteredData.sort((a, b) => {
      const getRank = (code: string) => {
        if (code.includes('prescription')) return 1;
        if (code.includes('missed')) return 3;
        if (code.includes('document')) return 4;
        return 2;
      };
      const rankA = getRank(a.code);
      const rankB = getRank(b.code);
      if (rankA !== rankB) return rankA - rankB;
      if (a.label !== b.label) return a.label.localeCompare(b.label);
      return a.price - b.price;
    });

    setSelectableProducts(sortedData);

    if (sortedData.length > 0 && show) {
      setFormData((current) => ({ ...current, productCode: sortedData[0].code }));
    }
  }, [products, show]);

  useEffect(() => {
    const product = products.find((p) => p.code === formData.productCode) || null;
    setSelectedProduct(product);
  }, [formData.productCode, products]);

  const formatPrice = (price: number): string => {
    return (price / 100).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const createPaymentText = intl.formatMessage({ id: 'patient.createPayment' });

  const handleCreatePayment = async () => {
    if (!formData.productCode) {
      setErrorMessage(intl.formatMessage({ id: 'patient.error.selectProduct' }));
      return;
    }
    if (selectedProduct?.isDoctorPayment === true && !formData.doctorId) {
      setErrorMessage(intl.formatMessage({ id: 'patient.error.selectDoctor' }));
      return;
    }
    if (isAppointment && !formData.appointmentId) {
      setErrorMessage(intl.formatMessage({ id: 'patient.error.enterAppointmentId' }));
      return;
    }
    if (isPrescription && !formData.followupPrescriptionId) {
      setErrorMessage(intl.formatMessage({ id: 'patient.error.enterPrescriptionId' }));
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const payload: PaymentRequestData = {
      patientId,
      productCode: formData.productCode,
    };

    if (isPrescription) {
      payload.followupPrescriptionId = Number(formData.followupPrescriptionId) || undefined;
    }

    if (isAppointment) {
      payload.appointmentId = Number(formData.appointmentId) || undefined;
    }

    if (!isDoctorPaymentFalse) {
      payload.doctorId = Number(formData.doctorId) || undefined;
    }

    if (formData.couponCode) {
      payload.couponCode = formData.couponCode;
    }

    try {
      const response = await PaymentService.createPaymentForPatient(payload);
      if (response.error) {
        setErrorMessage(response.message || intl.formatMessage({ id: 'patient.error.unknown' }));
      } else {
        toast.success(intl.formatMessage({ id: 'patient.info.creatingPayment' }));
        onSuccess();
        setShow(false);
      }
    } catch (error) {
      setErrorMessage(intl.formatMessage({ id: 'patient.error.createPayment' }));
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const getDoctorLabel = (doctor: Doctor) => {
    const relation = patientDoctorsList.find((rel) => rel.doctorId === doctor.id);
    const name = `${doctor.firstname} ${doctor.lastname}`;

    if (relation) {
      if (relation.doctorType === 1) return `${name} (Main Doctor)`;
      if (relation.doctorType === 2) return `${name} (Sub Doctor)`;
    }
    return name;
  };

  const errorStyle: React.CSSProperties = {
    color: '#9f3A38',
    backgroundColor: '#fff6f6',
    border: '1px solid #e0b4b4',
    borderRadius: '0.285rem',
    padding: '1em 1.5em',
    marginTop: '1rem',
    textAlign: 'center',
  };

  return (
    <FormDialog
      title={createPaymentText}
      onSave={handleCreatePayment}
      onClose={() => setShow(false)}
      setShow={setShow}
      show={show}
      textSave={isSubmitting ? `${createPaymentText}...` : createPaymentText}
      saveDisabled={isSubmitting}
    >
      <FormField
        type='select'
        name='productCode'
        value={formData.productCode}
        onChange={handleFieldChange}
        choices={selectableProducts.map((p) => {
          const label = `${p.label} (${formatPrice(p.price)})`;
          return [p.code, label];
        })}
      >
        Product *
      </FormField>

      {isAppointment && (
        <FormField type='number' name='appointmentId' value={formData.appointmentId} onChange={handleFieldChange}>
          Appointment ID *
        </FormField>
      )}

      {isPrescription && (
        <FormField
          type='number'
          name='followupPrescriptionId'
          value={formData.followupPrescriptionId}
          onChange={handleFieldChange}
        >
          Follow Up Prescription Request ID *
        </FormField>
      )}

      {!isDoctorPaymentFalse && (
        <FormField
          type='select'
          name='doctorId'
          value={formData.doctorId}
          onChange={handleFieldChange}
          choices={allDoctors.map((doc) => [doc.id.toString(), getDoctorLabel(doc)])}
        >
          Doctor *
        </FormField>
      )}

      <FormField
        type='select'
        name='couponCode'
        value={formData.couponCode}
        onChange={handleFieldChange}
        choices={DISCOUNT_CODE_CHOICES}
      >
        Discount Code
      </FormField>

      {errorMessage && <div style={errorStyle}>{errorMessage}</div>}
    </FormDialog>
  );
}
